"use client";

import { VideoPlaylistType } from "@/app/types/playlist";
import { Box, Divider, Paper, Skeleton, Stack } from "@mui/material";
import { ListVideo } from "lucide-react";
import { useEffect, useState } from "react";
import VideoPlaylistPanel from "./VideoPlaylistPanel";

type InitialPlaylist = {
    id: number;
    title: string;
};

type Props = {
    videoSlug: string;
    currentVideoId: number;
    initialPlaylists: InitialPlaylist[];
};

export default function VideoPlaylistSidebar({ videoSlug, currentVideoId, initialPlaylists }: Props) {
    const [playlists, setPlaylists] = useState<VideoPlaylistType[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetch(`${process.env.NEXT_PUBLIC_BASE_API_URL}/api/v1/video/${encodeURIComponent(videoSlug)}/playlists`)
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
                if (!data?.data) return;
                const sorted = (data.data as VideoPlaylistType[]).sort((a, b) => {
                    if (a.slug === "public") return 1;
                    if (b.slug === "public") return -1;
                    return 0;
                });
                setPlaylists(sorted.filter((p) => p.videos.length > 0));
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [videoSlug]);

    if (loading && playlists.length < 1) {
        return (
            <Stack gap={2} sx={{ p: 2, pt: { md: 0 } }}>
                {initialPlaylists.map((playlist) => (
                    <Paper key={playlist.id} variant="outlined" sx={{ overflow: "hidden" }}>
                        <Box sx={{ px: 2, py: 1.5, bgcolor: "grey.50" }}>
                            <Stack direction="row" alignItems="center" gap={0.75}>
                                <ListVideo size={16} />
                                <Skeleton width={120} height={20} />
                            </Stack>
                            <Skeleton width={50} height={16} />
                        </Box>
                        <Divider />
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    px: 2,
                                    py: 1,
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                    "&:last-child": { borderBottom: "none" },
                                }}
                            >
                                <Skeleton variant="rectangular" width={80} height={45} sx={{ flexShrink: 0, borderRadius: 0.5 }} />
                                <Skeleton width="100%" height={20} />
                            </Box>
                        ))}
                    </Paper>
                ))}
            </Stack>
        );
    }

    if (!playlists.length) return null;

    return <VideoPlaylistPanel playlists={playlists} currentVideoId={currentVideoId} />;
}
